import React from 'react'
import { Head, Link } from '@inertiajs/react'
import {
  Box, Container, Typography, Button, Stack, Card, Chip,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import PlaceIcon from '@mui/icons-material/Place'
import BrandLogo from '../../components/BrandLogo'
import naviFlag from '../../images/navi/navi_flag.png'

interface GroupRef { id: number; name: string }
interface LocationItem {
  id: number
  name: string
  address: string | null
  category: string | null
  thumbnail_url: string | null
  groups: GroupRef[]
}
interface Props {
  popular_locations: LocationItem[]
}

function TopNav() {
  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'rgba(250, 247, 255, 0.85)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(123, 91, 214, 0.10)',
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ minHeight: { xs: 64, md: 76 }, gap: 2 }}
        >
          <BrandLogo size="md" />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <Button
              component={Link}
              href="/users/sign_in"
              variant="text"
              size="small"
              sx={{ alignSelf: 'center', flexShrink: 0, px: 2, py: 0.75 }}
            >
              Sign in
            </Button>
            <Button
              component={Link}
              href="/users/sign_up"
              variant="contained"
              color="primary"
              size="small"
              sx={{ alignSelf: 'center', flexShrink: 0, px: 2.25, py: 0.75 }}
            >
              Register
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

function HeroSection({ featured }: { featured: LocationItem[] }) {
  const card1 = featured[0]
  const card2 = featured[1]
  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, pt: { xs: 5, md: 8 }, pb: { xs: 8, md: 12 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1.05fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Chip
            label="✨ Your K-Pop adventure starts here"
            sx={{
              backgroundColor: 'rgba(123, 91, 214, 0.10)',
              color: 'primary.main',
              fontWeight: 600,
              mb: 3,
            }}
          />
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              lineHeight: 1.1,
              mb: 2.5,
              fontSize: { xs: '2.25rem', md: '3rem', lg: '3.5rem' },
            }}
          >
            Explore{' '}
            <Box component="span" sx={{ color: 'primary.main' }}>
              K-Pop
            </Box>{' '}
            spots around the world
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: '1.05rem',
              mb: 4,
              maxWidth: 520,
            }}
          >
            From iconic cafés to filming locations, fan meet-ups and hidden gems — Navi
            knows them all. Save your favorites, plan your trips, and turn every visit
            into a memory.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              component={Link}
              href="/users/sign_up"
              variant="contained"
              color="primary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 3.5, py: 1.25, fontSize: '0.95rem' }}
            >
              Start Trending
            </Button>
            <Button
              component={Link}
              href="/users/sign_in"
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<PlaceIcon />}
              sx={{ px: 3.5, py: 1.25, fontSize: '0.95rem', backgroundColor: '#fff' }}
            >
              Sign in
            </Button>
          </Stack>
        </Box>

        <Box sx={{ position: 'relative', minHeight: { xs: 480, md: 640 } }}>
          {/* Soft glow behind mascot */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: '0% 4%',
              background: 'radial-gradient(circle at 50% 50%, rgba(123, 91, 214, 0.22) 0%, rgba(123, 91, 214, 0) 70%)',
              filter: 'blur(8px)',
              zIndex: 0,
            }}
          />
          <Box
            component="img"
            src={naviFlag}
            alt="Navi the fox mascot"
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: { xs: 480, md: 620, lg: 720 },
              display: 'block',
              mx: 'auto',
            }}
          />
          {card1 && (
            <Card
              sx={{
                position: 'absolute',
                top: { xs: 0, md: '6%' },
                left: { xs: 0, md: '-2%' },
                width: { xs: 170, md: 220 },
                p: 1.25,
                zIndex: 2,
                boxShadow: '0 12px 28px rgba(123, 91, 214, 0.18)',
                transform: 'rotate(-4deg)',
              }}
            >
              <Box
                sx={{
                  height: 110,
                  borderRadius: 2,
                  backgroundImage: card1.thumbnail_url
                    ? `url(${card1.thumbnail_url})`
                    : 'linear-gradient(135deg, #EDE6FE 0%, #FAF7FF 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <Box sx={{ px: 0.5, pt: 1, pb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                  {card1.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                  {card1.category || 'K-Pop spot'}
                </Typography>
              </Box>
            </Card>
          )}
          {card2 && (
            <Card
              sx={{
                position: 'absolute',
                bottom: { xs: 0, md: '6%' },
                right: { xs: 0, md: '-2%' },
                width: { xs: 170, md: 230 },
                p: 1.25,
                zIndex: 2,
                boxShadow: '0 12px 28px rgba(123, 91, 214, 0.18)',
                transform: 'rotate(3deg)',
              }}
            >
              <Box
                sx={{
                  height: 110,
                  borderRadius: 2,
                  backgroundImage: card2.thumbnail_url
                    ? `url(${card2.thumbnail_url})`
                    : 'linear-gradient(135deg, #EDE6FE 0%, #FAF7FF 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <Box sx={{ px: 0.5, pt: 1, pb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                  {card2.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                  {card2.category || 'K-Pop spot'}
                </Typography>
              </Box>
            </Card>
          )}
        </Box>
      </Box>
    </Container>
  )
}

function Landing({ popular_locations }: Props) {
  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Head title="KpopMap — explore K-Pop spots around the world" />
      <TopNav />
      <HeroSection featured={popular_locations} />
    </Box>
  )
}

// Tell the Inertia entrypoint not to wrap the page in the app `Layout`
// (sidebar/search bar). The landing page has its own marketing nav.
;(Landing as React.ComponentType & { noLayout?: boolean }).noLayout = true

export default Landing
